import React, { useState, useRef } from 'react';
import Grid from '@mui/material/Grid2'
import { Modal, Fade, Paper, Button, Backdrop, Divider, IconButton, CircularProgress, Box, Typography, Stepper, Step, StepLabel, Alert } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close'
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined'
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { setSuccessNotification, setErrorNotification } from '../../../actions/notificationAction';
import useTranslation from '../../../hooks/useTranslation'
import ImportReviewStep from './importReview/ImportReviewStep';

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

const styles = {
  modal: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: '10px',
    overflow: 'hidden',
  },
  modalStep1: {
    width: { xs: '95%', sm: '70%', md: '60%', lg: '46%' },
  },
  modalStep2: {
    width: { xs: '95%', sm: '92%', md: '90%', lg: '85%' },
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    padding: '20px 24px 16px',
  },
  stepper: {
    marginTop: '10px',
    maxWidth: '360px',
  },
  body: {
    padding: '8px 24px 24px',
  },
  dropzone: (isDragActive) => ({
    border: '2px dashed',
    borderColor: isDragActive ? 'primary.main' : 'divider',
    borderRadius: '8px',
    bgcolor: isDragActive ? 'action.hover' : 'transparent',
    padding: '36px 16px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'border-color 0.15s ease, background-color 0.15s ease',
    '&:hover': {
      borderColor: 'primary.main',
      bgcolor: 'action.hover',
    },
  }),
  inputFile: {
    display: 'none',
  },
  fileCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 14px',
    marginTop: '16px',
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: '8px',
  },
  hint: {
    display: 'block',
    marginTop: '16px',
  },
}

const UploadCsvModal = (props) => {
  const { title, modalState, setModalState, apiFunction, revalidateApiFunction, commitApiFunction, onImportComplete } = props;
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [isDragActive, setDragActive] = useState(false);
  const [isInviteChecked, setInviteChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [excludedRowIndexes, setExcludedRowIndexes] = useState(new Set());
  const [editingRowIndex, setEditingRowIndex] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const { t } = useTranslation()

  const resetState = () => {
    setStep(1);
    setFile(null);
    setErrorMessage('');
    setDragActive(false);
    setInviteChecked(false);
    setLoading(false);
    setRows([]);
    setStats(null);
    setExcludedRowIndexes(new Set());
    setEditingRowIndex(null);
    setEditDraft(null);
  };

  const closeModal = () => {
    setModalState(false);
    resetState();
  };

  const processFile = async (selectedFile) => {
    if (!selectedFile) return;
    if (!selectedFile.name.toLowerCase().endsWith('.csv') && selectedFile.type !== 'text/csv') {
      setErrorMessage(t('labels.import_preview.invalid_file_type'));
      return;
    }
    if (selectedFile.size > MAX_FILE_SIZE_BYTES) {
      setErrorMessage(t('labels.import_preview.file_too_large'));
      return;
    }
    setFile(selectedFile);
    setErrorMessage('');
    setLoading(true);
    const formData = new FormData();
    formData.append('file', selectedFile);
    try {
      const response = await apiFunction(formData);
      if (response.status === 200) {
        setRows(response.data.rows);
        setStats(response.data.stats);
        setExcludedRowIndexes(new Set());
        setStep(2);
      } else {
        setErrorMessage(response.data.message);
        dispatch(setErrorNotification({ message: response.data.message }))
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Something went wrong. Please try again later!';
      setErrorMessage(message);
      dispatch(setErrorNotification({ message }))
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => processFile(e.target.files[0]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    processFile(e.dataTransfer.files?.[0]);
  };

  const clearFile = (e) => {
    e.stopPropagation();
    setFile(null);
    setErrorMessage('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const onEditStart = (row) => {
    setEditingRowIndex(row.rowIndex);
    setEditDraft({ ...row.data });
  };

  const onEditFieldChange = (name, value) => {
    setEditDraft((prev) => ({ ...prev, [name]: value }));
  };

  const onEditCancel = () => {
    setEditingRowIndex(null);
    setEditDraft(null);
  };

  const onEditSave = async () => {
    setLoading(true);
    try {
      const updatedRows = rows
        .map((row) => (row.rowIndex === editingRowIndex ? { ...row, data: { ...editDraft } } : row))
        .map((row) => row.data);
      const response = await revalidateApiFunction(updatedRows);
      if (response.status === 200) {
        setRows(response.data.rows);
        setStats(response.data.stats);
        setEditingRowIndex(null);
        setEditDraft(null);
      } else {
        dispatch(setErrorNotification({ message: response.data.message }))
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Something went wrong. Please try again later!';
      dispatch(setErrorNotification({ message }))
    } finally {
      setLoading(false);
    }
  };

  const handleCommit = async () => {
    setLoading(true);
    try {
      const acceptedRows = rows
        .filter((row) => !row.blocked && !excludedRowIndexes.has(row.rowIndex))
        .map((row) => row.data);
      const response = await commitApiFunction({
        rows: acceptedRows,
        inviteUser: isInviteChecked,
      });
      if (response.status === 200) {
        dispatch(setSuccessNotification({ message: response.data.message }))
        onImportComplete && onImportComplete();
        closeModal();
      } else {
        dispatch(setErrorNotification({ message: response.data.message }))
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Something went wrong. Please try again later!';
      dispatch(setErrorNotification({ message }))
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={modalState}
      onClose={closeModal}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
    >
      <Fade in={modalState}>
        <Paper sx={{ ...styles.modal, ...(step === 1 ? styles.modalStep1 : styles.modalStep2) }}>
          <Backdrop sx={{ position: 'absolute', color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
            <CircularProgress color="inherit" />
          </Backdrop>
          <Grid container>
            <Grid size={12} sx={styles.header}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>{title}</Typography>
                <Stepper activeStep={step - 1} sx={styles.stepper}>
                  <Step><StepLabel>{t('labels.import_preview.step_upload')}</StepLabel></Step>
                  <Step><StepLabel>{t('labels.import_preview.step_review')}</StepLabel></Step>
                </Stepper>
              </Box>
              <IconButton onClick={closeModal} size="small"><CloseIcon /></IconButton>
            </Grid>
            <Divider width='100%' />

            {step === 1 && (
              <Grid size={12} sx={styles.body}>
                <input ref={fileInputRef} style={styles.inputFile} type="file" onChange={handleFileChange} accept=".csv" />
                <Box
                  sx={styles.dropzone(isDragActive)}
                  onClick={() => fileInputRef.current.click()}
                  onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                  onDragLeave={() => setDragActive(false)}
                  onDrop={handleDrop}
                >
                  <CloudUploadOutlinedIcon sx={{ fontSize: 44, color: 'primary.main', marginBottom: '8px' }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{t('labels.import_preview.dropzone_title')}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ marginBottom: '16px' }}>{t('labels.import_preview.dropzone_subtitle')}</Typography>
                  <Button variant="outlined" onClick={(e) => { e.stopPropagation(); fileInputRef.current.click(); }}>
                    {t('labels.import_preview.choose_file')}
                  </Button>
                </Box>

                {file && (
                  <Box sx={styles.fileCard}>
                    <InsertDriveFileOutlinedIcon color="action" />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" noWrap>{file.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{(file.size / 1024).toFixed(1)} KB</Typography>
                    </Box>
                    {loading
                      ? <CircularProgress size={20} />
                      : <IconButton size="small" onClick={clearFile}><CloseIcon fontSize="small" /></IconButton>}
                  </Box>
                )}

                {loading && (
                  <Typography variant="body2" color="text.secondary" sx={{ marginTop: '12px' }}>
                    {t('labels.import_preview.analyzing')}
                  </Typography>
                )}

                {errorMessage && <Alert severity="error" sx={{ marginTop: '16px' }}>{errorMessage}</Alert>}

                <Typography variant="caption" color="text.secondary" sx={styles.hint}>
                  {t('labels.import_preview.dropzone_hint')}
                </Typography>
              </Grid>
            )}

            {step === 2 && (
              <ImportReviewStep
                rows={rows}
                stats={stats}
                excludedRowIndexes={excludedRowIndexes}
                setExcludedRowIndexes={setExcludedRowIndexes}
                editingRowIndex={editingRowIndex}
                editDraft={editDraft}
                onEditStart={onEditStart}
                onEditFieldChange={onEditFieldChange}
                onEditCancel={onEditCancel}
                onEditSave={onEditSave}
                isInviteChecked={isInviteChecked}
                setInviteChecked={setInviteChecked}
                onBack={resetState}
                onCommit={handleCommit}
                loading={loading}
              />
            )}
          </Grid>
        </Paper>
      </Fade>
    </Modal>
  );
}

UploadCsvModal.propTypes = {
  apiFunction: PropTypes.func.isRequired,
  revalidateApiFunction: PropTypes.func.isRequired,
  commitApiFunction: PropTypes.func.isRequired,
  onImportComplete: PropTypes.func,
}

export default UploadCsvModal;
